import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import '../css/AddUnit.css';

const AddMenu = () => {
  const navigate = useNavigate();
  const [menuName, setMenuName] = useState('');
  const [price, setPrice] = useState('');
  const [photo, setPhoto] = useState(null);
  const [categoryId, setCategoryId] = useState('');
  const [roomTypeId, setRoomTypeId] = useState('');
  const [categories, setCategories] = useState([]);
  const [roomTypes, setRoomTypes] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', type: '' });

  useEffect(() => {
    // Fetch categories from API
    const fetchCategories = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}list-categories.php`);
        if (!res.ok) throw new Error('Network response was not ok');
        const data = await res.json();
        if (data.status && Array.isArray(data.categories)) {
          setCategories(data.categories);
        } else {
          showToast('Failed to load categories', 'error');
        }
      } catch (err) {
        showToast('Error fetching categories', 'error');
      }
    };

    // Fetch room types from API
    const fetchRoomTypes = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}list-room-types.php`);
        if (!res.ok) throw new Error('Network response was not ok');
        const data = await res.json();
        if (data.status && Array.isArray(data.room_types)) {
          setRoomTypes(data.room_types);
        } else {
          showToast('Failed to load room types', 'error');
        }
      } catch (err) {
        showToast('Error fetching room types', 'error');
      }
    };

    fetchCategories();
    fetchRoomTypes();
  }, []);

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
    setTimeout(() => {
      setToast({ show: false, message: '', type: '' });
    }, 3000);
  };

  const handlePhotoChange = (e) => {
    setPhoto(e.target.files[0]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!menuName.trim()) {
      showToast('Please enter a menu name', 'error');
      return;
    }
    if (!price || isNaN(price) || Number(price) <= 0) {
      showToast('Please enter a valid price', 'error');
      return;
    }
    if (!photo) {
      showToast('Please upload a photo', 'error');
      return;
    }
    if (!categoryId) {
      showToast('Please select a category', 'error');
      return;
    }
    if (!roomTypeId) {
      showToast('Please select a room type', 'error');
      return;
    }
    setIsSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('category_id', categoryId);
      formData.append('room_type_id', roomTypeId);
      formData.append('menuname', menuName);
      formData.append('price', price);
      formData.append('image', photo);

      const response = await fetch(`${process.env.REACT_APP_API_URL}add-menu.php`, {
        method: 'POST',
        body: formData,
      });
      const data = await response.json();
      if (data.status) {
        showToast(`Menu item "${menuName}" added successfully!`, 'success');
        setMenuName('');
        setPrice('');
        setPhoto(null);
        setCategoryId('');
        setRoomTypeId('');
        setTimeout(() => {
          navigate('/menu-list');
        }, 1500);
      } else {
        showToast(data.message || 'Error adding menu item. Please try again.', 'error');
      }
    } catch (error) {
      showToast('Error adding menu item. Please try again.', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="add-menu-content">
      <div className="page-header" style={{display: 'block'}}>
        <h1>Add Menu Item</h1>
        <p>Add a new menu item to your restaurant</p>
      </div>
      <div className="form-container">
        <form onSubmit={handleSubmit} className="add-unit-form" encType="multipart/form-data">
          <div className="form-group">
            <label htmlFor="menuName">Menu Name</label>
            <input
              type="text"
              id="menuName"
              name="menuName"
              value={menuName}
              onChange={(e) => setMenuName(e.target.value)}
              placeholder="Enter menu name"
              required
              disabled={isSubmitting}
            />
          </div>
          <div className="form-group">
            <label htmlFor="price">Price</label>
            <input
              type="number"
              id="price"
              name="price"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              placeholder="Enter price"
              min="0.01"
              step="0.01"
              required
              disabled={isSubmitting}
            />
          </div>
          <div className="form-group">
            <label htmlFor="photo">Photo</label>
            <input
              type="file"
              id="photo"
              name="photo"
              accept="image/*"
              onChange={handlePhotoChange}
              required
              disabled={isSubmitting}
            />
          </div>
          <div className="form-group">
            <label htmlFor="category">Category</label>
            <select
              id="category"
              name="category"
              value={categoryId}
              onChange={(e) => setCategoryId(e.target.value)}
              required
              disabled={isSubmitting}
            >
              <option value="">Select category</option>
              {categories.map((cat) => (
                <option key={cat.id} value={cat.id}>{cat.categoryname}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label htmlFor="roomType">Room Type</label>
            <select
              id="roomType"
              name="roomType"
              value={roomTypeId}
              onChange={(e) => setRoomTypeId(e.target.value)}
              required
              disabled={isSubmitting}
            >
              <option value="">Select room type</option>
              {roomTypes.map((roomType) => (
                <option key={roomType.id} value={roomType.id}>{roomType.type}</option>
              ))}
            </select>
          </div>
          <button 
            type="submit" 
            className="submit-btn"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Adding Menu...' : 'Add Menu'}
          </button>
        </form>
      </div>
      {toast.show && (
        <div className={`toast toast-${toast.type}`}>
          <div className="toast-content">
            <span className="toast-message">{toast.message}</span>
            <button 
              className="toast-close" 
              onClick={() => setToast({ show: false, message: '', type: '' })}
            >
              ×
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddMenu; 